import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import OpenPlayToolsLayout from '../../components/openplay/OpenPlayToolsLayout';
import { supabase } from '../../lib/supabaseClient';
import EventTypeInviteForm from './components/EventTypeInviteForm';
import { resizeImageToWidth } from '../../lib/imageUtils';

const normalizeType = (eventType) => {
  const t = String(eventType || '').trim().toLowerCase();
  if (['lesson', 'training'].includes(t)) return t;
  return 'lesson';
};

const labelForType = (eventType) => {
  const t = normalizeType(eventType);
  if (t === 'lesson') return '레슨';
  if (t === 'training') return '트레이닝';
  return '레슨';
};

const EventTypeCreatePage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const type = useMemo(() => normalizeType(params?.type), [params?.type]);
  const editId = useMemo(() => {
    const v = searchParams.get('id');
    return v ? String(v) : '';
  }, [searchParams]);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [initialValues, setInitialValues] = useState(null);

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      if (!editId) {
        setInitialValues(null);
        return;
      }

      setLoading(true);
      try {
        const trySelect = async (selectClause) => {
          return supabase
            .from('session_invites')
            .select(selectClause)
            .eq('id', editId)
            .maybeSingle();
        };

        let result = await trySelect(
          'id, token, title, date, end_date, start_time, end_time, venue, venue_address, venue_lat, venue_lng, description, url, event_type, cover_image_url, image_urls'
        );

        if (result?.error?.message && String(result.error.message).includes('session_invites.url')) {
          result = await trySelect(
            'id, token, title, date, end_date, start_time, end_time, venue, venue_address, venue_lat, venue_lng, description, event_type, cover_image_url, cover_image_thumb_url, image_urls, price, bank_name, account_number, account_holder, payment_deadline'
          );
        }

        const { data, error } = result || {};

        if (!mounted) return;
        if (error || !data) {
          alert(error?.message || '이벤트 정보를 불러오지 못했습니다.');
          navigate('/session-list-dashboard/invite');
          return;
        }

        setInitialValues({
          title: String(data?.title || ''),
          date: String(data?.date || ''),
          endDate: String(data?.end_date || ''),
          startTime: String(data?.start_time || ''),
          endTime: String(data?.end_time || ''),
          locationName: String(data?.venue || ''),
          address: String(data?.venue_address || ''),
          venueLat: data?.venue_lat != null ? String(data.venue_lat) : '',
          venueLng: data?.venue_lng != null ? String(data.venue_lng) : '',
          description: String(data?.description || ''),
          url: String(data?.url || ''),
          memberPrice: data?.price != null ? String(data.price) : '',
          bankName: String(data?.bank_name || ''),
          accountNumber: String(data?.account_number || ''),
          accountHolder: String(data?.account_holder || ''),
          paymentDeadline: (() => {
            if (!data?.payment_deadline) return '';
            try {
              return new Date(data.payment_deadline).toISOString().slice(0, 16);
            } catch {
              return '';
            }
          })(),
          existingImageUrls: Array.isArray(data?.image_urls) ? data.image_urls : [],
          existingCoverImageUrl: String(data?.cover_image_url || ''),
          existingCoverImageThumbUrl: String(data?.cover_image_thumb_url || ''),
        });
      } finally {
        if (mounted) setLoading(false);
      }
    };

    run();

    return () => {
      mounted = false;
    };
  }, [editId, navigate]);

  const handleSubmit = async ({ payload, generateToken, images }) => {
    try {
      setSubmitting(true);

      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) {
        alert('로그인이 필요합니다. 다시 로그인 후 시도해주세요.');
        return;
      }

      const getFileExt = (file) => {
        const type = String(file?.type || '').toLowerCase();
        if (type === 'image/webp') return 'webp';
        if (type === 'image/png') return 'png';
        if (type === 'image/jpeg' || type === 'image/jpg') return 'jpg';
        if (type === 'image/gif') return 'gif';
        return 'jpg';
      };

      const drafts = Array.isArray(images?.drafts) ? images.drafts : [];
      const keptUrls = Array.isArray(images?.keptUrls) ? images.keptUrls : [];
      const coverSelection = images?.coverSelection || null;

      if (drafts.length + keptUrls.length > 10) {
        alert('이미지는 최대 10장까지 등록할 수 있습니다.');
        return;
      }

      const bucket = 'session-images';
      const uploadDrafts = async (sessionId) => {
        const uploaded = [];
        for (let i = 0; i < drafts.length; i += 1) {
          const draft = drafts[i];
          if (!draft?.file) continue;
          const ext = getFileExt(draft.file);
          const path = `${userId}/sessions/${sessionId}/${generateToken()}.${ext}`;
          const { error: uploadError } = await supabase.storage.from(bucket).upload(path, draft.file, {
            cacheControl: '3600',
            upsert: false,
            contentType: draft.file?.type || 'image/jpeg',
          });
          if (uploadError) {
            throw new Error(uploadError?.message || '이미지 업로드에 실패했습니다.');
          }
          const { data: publicData } = supabase.storage.from(bucket).getPublicUrl(path);
          const publicUrl = String(publicData?.publicUrl || '').trim();
          if (publicUrl) uploaded.push({ id: draft.id, url: publicUrl, file: draft.file });
        }
        return uploaded;
      };

      const uploadCoverThumb = async ({ sessionId, coverFile }) => {
        if (!coverFile) return null;
        const thumbFile = await resizeImageToWidth(coverFile, {
          width: 100,
          mimeType: 'image/jpeg',
          quality: 0.82,
          fileName: 'thumb.jpg',
        });
        const path = `${userId}/sessions/${sessionId}/thumb_${generateToken()}.jpg`;
        const { error: uploadError } = await supabase.storage.from(bucket).upload(path, thumbFile, {
          cacheControl: '3600',
          upsert: false,
          contentType: 'image/jpeg',
        });
        if (uploadError) {
          throw new Error(uploadError?.message || '대표 이미지 썸네일 업로드에 실패했습니다.');
        }
        const { data: publicData } = supabase.storage.from(bucket).getPublicUrl(path);
        return String(publicData?.publicUrl || '').trim() || null;
      };

      const resolveCoverUrl = ({ finalUrls, uploadedById }) => {
        if (coverSelection?.kind === 'existing') {
          const url = String(coverSelection?.value || '').trim();
          if (url && finalUrls.includes(url)) return url;
        }
        if (coverSelection?.kind === 'draft') {
          const id = String(coverSelection?.value || '').trim();
          const url = uploadedById.get(id);
          if (url && finalUrls.includes(url)) return url;
        }
        return finalUrls[0] || null;
      };

      const resolveCoverFile = ({ coverUrl, uploaded, finalUrls }) => {
        if (!coverUrl) return null;
        if (coverSelection?.kind === 'draft') {
          const id = String(coverSelection?.value || '').trim();
          const hit = uploaded.find((u) => String(u?.id || '').trim() === id);
          return hit?.file || null;
        }
        const match = uploaded.find((u) => String(u?.url || '').trim() === String(coverUrl || '').trim());
        if (match?.file) return match.file;
        const first = uploaded[0];
        if (first?.file && finalUrls?.[0] && String(finalUrls[0]).trim() === String(first.url || '').trim()) {
          return first.file;
        }
        return null;
      };

      if (editId) {
        const uploaded = await uploadDrafts(editId);
        const uploadedUrls = uploaded.map((u) => u.url);
        const finalUrls = [...keptUrls, ...uploadedUrls].filter(Boolean).slice(0, 10);
        const uploadedById = new Map(uploaded.map((u) => [u.id, u.url]));
        const coverUrl = resolveCoverUrl({ finalUrls, uploadedById });

        let coverThumbUrl = payload?.cover_image_thumb_url || null;
        const coverFile = resolveCoverFile({ coverUrl, uploaded, finalUrls });
        if (coverFile) {
          coverThumbUrl = await uploadCoverThumb({ sessionId: editId, coverFile });
        }

        const { error } = await supabase
          .from('session_invites')
          .update({
            ...payload,
            event_type: type,
            cover_image_url: coverUrl,
            cover_image_thumb_url: coverThumbUrl,
            image_urls: finalUrls,
          })
          .eq('id', editId);

        if (error) {
          alert(error?.message || '수정에 실패했습니다.');
          return;
        }

        alert('수정되었습니다.');
        navigate('/session-list-dashboard/invite');
        return;
      }

      const insertPayload = {
        client_id: userId,
        token: generateToken(),
        status: 'open',
        registered: 0,
        participants: [],
        ...payload,
        event_type: type,
      };

      const { data: created, error } = await supabase.from('session_invites').insert(insertPayload).select('id').single();
      if (error || !created?.id) {
        alert(error?.message || '등록에 실패했습니다.');
        return;
      }

      const uploaded = await uploadDrafts(created.id);
      const uploadedUrls = uploaded.map((u) => u.url);
      const finalUrls = [...keptUrls, ...uploadedUrls].filter(Boolean).slice(0, 10);
      const uploadedById = new Map(uploaded.map((u) => [u.id, u.url]));
      const coverUrl = resolveCoverUrl({ finalUrls, uploadedById });

      const coverFile = resolveCoverFile({ coverUrl, uploaded, finalUrls });
      const coverThumbUrl = coverFile
        ? await uploadCoverThumb({ sessionId: created.id, coverFile })
        : null;

      const { error: patchError } = await supabase
        .from('session_invites')
        .update({
          cover_image_url: coverUrl,
          cover_image_thumb_url: coverThumbUrl,
          image_urls: finalUrls,
        })
        .eq('id', created.id)
        .eq('client_id', userId);

      if (patchError) {
        alert(patchError?.message || '이미지 저장에 실패했습니다.');
        return;
      }

      alert('등록되었습니다.');
      navigate('/session-list-dashboard/invite');
    } catch (e) {
      alert(e?.message || '저장 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!type) return null;

  return (
    <OpenPlayToolsLayout userRole="player">
      <main className="max-w-5xl mx-auto px-4 pt-6 md:pt-10 pb-6">
        <div className="bg-card border border-border rounded-2xl shadow-sm p-5 md:p-6 mb-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
                {labelForType(type)} {editId ? '수정' : '등록'}
              </h1>
              <p className="text-sm caption text-muted-foreground mt-2">
                시작일시/종료일시, 장소(네이버 검색), 상세 설명을 입력하세요.
              </p>
            </div>

            <button
              type="button"
              onClick={() => navigate('/session-list-dashboard/invite')}
              className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground px-3 py-2 rounded-lg hover:bg-muted transition-athletic"
            >
              <Icon name="ChevronLeft" size={18} />
              목록으로
            </button>
          </div>
        </div>

        {loading ? (
          <div className="bg-card border border-border rounded-xl p-8 text-center">
            <p className="text-sm text-muted-foreground">불러오는 중...</p>
          </div>
        ) : (
          <EventTypeInviteForm
            supabase={supabase}
            eventType={type}
            initialValues={initialValues}
            submitting={submitting}
            onSubmit={handleSubmit}
          />
        )}
      </main>
    </OpenPlayToolsLayout>
  );
};

export default EventTypeCreatePage;
